import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

function useQuery() {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export function useUrlManager() {
  const history = useHistory()
  const location = useLocation()
  const currentQueryParams = useQuery()

  const deleteQueryParam = (key: string) => {
    currentQueryParams.delete(key)
    history.replace({
      pathname: location.pathname,
      search: currentQueryParams.toString(),
    })
  }

  const deleteMultipleQueryParams = (keys: string[]) => {
    for (const key of keys) {
      deleteQueryParam(key)
    }
  }

  const setParam = (
    param: string,
    value: string,
    { replace = true }: { replace?: boolean } = {}
  ) => {
    let setter: URLSearchParams

    if (currentQueryParams) {
      setter = currentQueryParams
    } else {
      setter = new URLSearchParams()
    }

    if (value === '' || !value) {
      return deleteQueryParam(param)
    }

    setter.set(param, value)

    const nextLocation = {
      pathname: location.pathname,
      search: setter.toString(),
    }

    if (replace) {
      history.replace(nextLocation)
    } else {
      history.push(nextLocation)
    }
  }

  /**
   * Applies several param changes as ONE history operation.
   *
   * `setParam` mutates the memoized `URLSearchParams` and pushes per call, so
   * changing three params in a row leaves three entries on the history stack —
   * the user then has to press Back three times to undo one view switch — and
   * leaves the memo carrying values the URL may not have. This clones first and
   * writes once. A null/undefined/empty value removes the key.
   */
  const setParams = (
    updates: Record<string, string | null | undefined>,
    { replace = true }: { replace?: boolean } = {}
  ) => {
    const next = new URLSearchParams(currentQueryParams.toString())

    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === undefined || value === '') {
        next.delete(key)
      } else {
        next.set(key, value)
      }
    }

    const search = next.toString()
    if (search === currentQueryParams.toString()) {
      return
    }

    const nextLocation = { pathname: location.pathname, search }

    if (replace) {
      history.replace(nextLocation)
    } else {
      history.push(nextLocation)
    }
  }

  return {
    currentQueryParams,
    setParam,
    setParams,
    deleteMultipleQueryParams,
  }
}
